import { BUILT_GROUPS } from "@/lib/built-catalogue";
import { CASE_STUDIES } from "@/lib/case-studies";
import { SERVICES } from "@/lib/services";

/**
 * THE MARKETS WE ARE DELIBERATELY GOING AFTER, AND WHAT WE CAN HONESTLY SAY ABOUT EACH.
 *
 * ────────────────────────────────────────────────────────────────────────────────
 *  WHEN AN INDUSTRY PAGE IS ALLOWED TO EXIST. It used to be one rule, written in
 *  `sectors.ts`: at least one live, named, checkable build in that sector. That
 *  rule was protecting against a real failure, a page with nothing behind it that
 *  has to be vague, and vague is worse than absent. It is not the rule any more,
 *  because it also blocked a market we have decided to go after and can genuinely
 *  build for.
 *
 *  A page may exist if EITHER
 *    A. there is genuine delivered proof in that sector, OR
 *    B. it is a market we are deliberately targeting, we have the technical
 *       capability to build useful systems for it, and there is enough specific
 *       knowledge of how those businesses run to make a page worth reading.
 *
 *  What did NOT change, and must not: proof is still proof. A delivered-work
 *  section is generated from real case studies and real catalogue entries, and
 *  disappears completely when there are none. Nothing borrows a project from
 *  another sector to look experienced. B buys the right to describe capability,
 *  never the right to imply delivery.
 * ────────────────────────────────────────────────────────────────────────────────
 *
 * TWO INDUSTRIES DELIBERATELY HAVE NO PAGE HERE. `/coaches` and `/accountants`
 * already exist, are the strongest pages we have in their markets, and carry
 * whatever equity they have earned. Consistency of route shape is not worth an
 * avoidable redirect, so they are listed with their real `href`, the hub links
 * straight to them, and the generator skips them.
 *
 * ACCOUNTANCY SITS UNDER PROFESSIONAL SERVICES RATHER THAN BESIDE IT, for the
 * same reason: two pages competing for one query is the thing this architecture
 * exists to avoid. Professional services is the broader market and says so, and
 * it points at `/accountants` as the narrower page where we have actually
 * delivered. Their search intent is different and so is their content.
 */

export type IndustryPage = {
  heading: string;
  metaTitle: string;
  metaDescription: string;
  /** One or two short sentences to the owner, naming the feeling before anything about us. */
  intro: string;
  /** How these businesses actually run. Sector specific, or the page has no reason to exist. */
  howTheyRun: readonly string[];
  /**
   * Where the work goes wrong, as short everyday moments in the owner's own words, four or five
   * of them (Tom, 2026-10-30: "speak into the actual business owner and get them to say yes in
   * their head"). Second person is fine and intended; a figure, or a claim about this reader's
   * own numbers, is not.
   */
  whereItHurts: readonly string[];
  /** CAPABILITY. Things we can build for this market. Never a claim of delivery. */
  canBuild: readonly string[];
  /** How a project in this market would run. */
  approach: readonly string[];
  /** EVIDENCE. Catalogue ids, validated. Empty means the proof section does not render. */
  builtIds: readonly string[];
  /** EVIDENCE. Case study slugs, validated. Empty means the work section does not render. */
  caseStudySlugs: readonly string[];
  /** Services worth reading next, by service slug. Validated. */
  relatedServiceSlugs: readonly string[];
  /** A narrower page inside this market, where one exists and has earned it. */
  moreSpecific?: { name: string; href: `/${string}`; note: string };
  /**
   * EVIDENCE YOU CAN SEE. Real screenshots of one of our own demonstrations, taken from the
   * live deployment, never mocked up. The company in it must be INVENTED (see demo-films.ts):
   * a demo built for a real named firm never goes on this site.
   */
  showcase?: IndustryShowcase;
  /**
   * Questions a buyer in this market actually asks, answered from what the demonstration or the
   * delivered work really does. Rendered on the page AND as FAQPage data, so never add one that is
   * not visible, and never answer with a feature that has no source behind it.
   */
  faq?: readonly { question: string; answer: string }[];
  /** The closing card leads with booking a review rather than the briefing. Search pages only. */
  bookFirst?: boolean;
};

export type IndustryShowcase = {
  heading: string;
  body: string;
  /** The invented company, named so the caption can say it is invented. */
  demoName: string;
  demoHref: `https://${string}`;
  desktop: { src: `/industries/${string}`; alt: string; width: number; height: number };
  phone: { src: `/industries/${string}`; alt: string; width: number; height: number };
};

export type Industry = {
  slug: string;
  href: `/${string}`;
  name: string;
  /** One line on the hub. */
  hubBlurb: string;
  /**
   * True when the page rests on delivered work in this sector, false when it rests on
   * capability. Read by the hub so it can be honest about which is which without anybody
   * having to remember to write it, and by the page to decide whether proof sections exist.
   */
  hasDeliveredWork: boolean;
  /** Absent for industries whose page already lives somewhere else. */
  page?: IndustryPage;
};

export const INDUSTRIES: readonly Industry[] = [
  {
    slug: "construction-and-trades",
    href: "/industries/construction-and-trades",
    name: "Construction and trades",
    hubBlurb:
      "Quotes, jobs and customer updates for trades who would rather be on site than at the kitchen table.",
    hasDeliveredWork: true,
    page: {
      heading: "Software for construction and trades businesses",
      metaTitle: "Software for construction and trades | Ownly Digital",
      metaDescription:
        "Custom software for construction and trades: estimating and quoting, job management, scheduling and customer portals, built around how your work runs.",
      intro:
        "You're pricing jobs at ten at night and chasing variations nobody wrote down. Meanwhile the customer's ringing to ask when you're starting.",
      howTheyRun: [
        "Every enquiry needs pricing before you know if it's worth having, and the price depends on measurements, materials and who's free.",
        "The job then runs across people who are rarely in one place: estimator, site, supplier, customer. The office is where it's all meant to meet, and where it usually stops.",
        "Money comes in stages, so deposits, variations and retentions all need tracking against a job that's still moving.",
      ],
      whereItHurts: [
        "Quotes taking days because the numbers are in your head.",
        "A variation agreed on site that nobody ever invoices.",
        "Customers ringing for an update, so you ring the site.",
        "Job photos stuck on phones instead of against the job.",
        "The diary's a whiteboard, and the whiteboard isn't in the van.",
      ],
      canBuild: [
        "Pricing rules that give the same number every time",
        "Quotes out the same day, with follow-ups that send themselves",
        "One record per job: the quote, variations, photos and paperwork",
        "A schedule showing who's where, that works on a phone",
        "A customer page with the job status and next date, so the update calls stop",
        "Photos and notes from site that upload when the signal comes back",
        "Deposits, stage payments and reminders tied to each job",
      ],
      approach: [
        "We start by following one real job end to end, including the bits that only work because someone rings someone.",
        "Then the smallest useful piece goes live first, usually quoting, because it eats the most time.",
      ],
      builtIds: [],
      caseStudySlugs: ["meridian-supply-co", "mew-installations"],
      relatedServiceSlugs: [
        "quoting-and-estimating",
        "internal-business-systems",
        "client-portals",
      ],
      moreSpecific: {
        name: "Property maintenance companies",
        href: "/industries/property-maintenance",
        note: "The narrower page, for firms doing repairs and maintenance for letting agents, housing providers and landlords.",
      },
    },
  },
  {
    slug: "professional-services",
    href: "/industries/professional-services",
    name: "Professional services",
    hubBlurb:
      "Onboarding, portals and document chasing for practices tired of asking clients for the same thing twice.",
    hasDeliveredWork: true,
    page: {
      heading: "Software for professional services firms",
      metaTitle: "Software for professional services firms | Ownly Digital",
      metaDescription:
        "Custom software for professional services: client onboarding, secure portals, document and approval workflows and recurring work, built around your practice.",
      intro:
        "You're paid for your judgement and spend half the week chasing documents. Clients email to ask where things are up to, one at a time.",
      howTheyRun: [
        "Work is either a matter with an end, or a recurring deadline that turns up whether you're ready or not. Most firms run both.",
        "Nearly every step needs something from the client, so chasing is the hidden job underneath everything.",
        "Confidentiality isn't optional. Documents need to move safely, and who saw what has to be answerable later.",
      ],
      whereItHurts: [
        "Onboarding stalls the moment one person's on holiday.",
        "Asking for a document the client already sent.",
        "Deadlines tracked in one person's spreadsheet.",
        "Answering “where are we up to?” emails all day.",
        "Sending sensitive files as email attachments, again.",
      ],
      canBuild: [
        "Client onboarding with the chasing built in",
        "A secure portal where each client sees their own work and next step",
        "Document uploads by link, with no client account needed",
        "E-signature on engagement letters, filed against the client automatically",
        "Recurring deadlines with early warnings, not late surprises",
        "Reporting on capacity and what's actually at risk",
        "Permissions, so staff see their clients and partners see the practice",
      ],
      approach: [
        "We start with the step where clients hold things up, because that's usually where the time goes.",
        "Everything keeps a record of who did what and when. In a practice, that's what makes a system worth trusting.",
      ],
      builtIds: [
        "client-portal",
        "client-intake-forms",
        "token-link-documents",
        "documents-behind-a-gate",
        "esign-retainer",
        "automatic-chasing",
        "ie-audit-trail",
        "mta-one-next-step-per-page",
      ],
      caseStudySlugs: ["my-trusted-accountant"],
      relatedServiceSlugs: [
        "client-portals",
        "internal-business-systems",
        "business-process-automation",
      ],
      moreSpecific: {
        name: "Accountancy practices",
        href: "/accountants",
        note: "The narrower page, and the one with the delivered work behind it.",
      },
    },
  },
  {
    slug: "recruitment",
    href: "/industries/recruitment",
    name: "Recruitment",
    hubBlurb:
      "Candidates, clients and placement admin, for agencies whose consultants spend more time updating records than talking to people.",
    hasDeliveredWork: false,
    page: {
      heading: "Software for recruitment businesses",
      metaTitle: "Software for recruitment agencies | Ownly Digital",
      metaDescription:
        "Custom software for recruitment agencies: candidate and client workflows, vacancies, compliance documents, placement tracking and far less repeated admin.",
      intro:
        "You got into recruitment to place people, not to keep two pipelines up to date all day. The first CV in usually wins, and the admin keeps making yours late.",
      howTheyRun: [
        "Every consultant juggles candidates and clients at once, so the system has to hold both against one vacancy.",
        "Speed decides it. The first good CV in front of a hiring manager usually beats the best one sent on Friday.",
        "Right to work, references, contracts and timesheets sit between a placement and an invoice. None of it is the job, and all of it holds up the money.",
      ],
      whereItHurts: [
        "The same candidate on the system three times.",
        "Candidates who didn't get it never hearing back.",
        "A handover that loses half the notes in someone's inbox.",
        "Chasing right to work documents the day before a start.",
        "Reading three lists to work out today's priority.",
      ],
      canBuild: [
        "One record per candidate and per client, with duplicates merged",
        "A pipeline for every role, not just every consultant",
        "Submissions in minutes, from what's already on the record",
        "Automatic updates for candidates who missed out, so they come back",
        "Compliance checklists that block the step you can't skip",
        "Document collection by link, with no candidate login needed",
        "Dashboards by consultant, client and role, built from the real work",
      ],
      approach: [
        "We start where the delay really is: between a candidate saying yes and a client seeing them.",
        "Consultants only keep a system up to date if it saves them time on day one, so that's what gets built first.",
      ],
      builtIds: [],
      caseStudySlugs: [],
      relatedServiceSlugs: [
        "crm-development",
        "business-process-automation",
        "internal-business-systems",
      ],
    },
  },
  {
    slug: "fitness-and-coaching",
    /* The existing page. Stronger than anything written from scratch and already ranked. */
    href: "/coaches",
    name: "Fitness and coaching",
    hubBlurb:
      "Programmes, check-ins and client apps for coaches tired of chasing clients on WhatsApp, with real coaching businesses running on them.",
    hasDeliveredWork: true,
  },
  {
    slug: "events-and-entertainment",
    href: "/industries/events-and-entertainment",
    name: "Events and entertainment",
    hubBlurb:
      "Enquiries, bookings and ticketing for events businesses tired of chasing deposits and run sheets.",
    hasDeliveredWork: true,
    page: {
      heading: "Software for events and entertainment businesses",
      metaTitle: "Software for events and entertainment | Ownly Digital",
      metaDescription:
        "Custom software for events and entertainment: enquiries and bookings, ticketing and door scanning, artists and suppliers, deposits and client updates.",
      intro:
        "You sell a brilliant night, then spend months chasing deposits, run sheets and suppliers to make sure it happens.",
      howTheyRun: [
        "An enquiry is a date, a place and a feeling, and two or three other suppliers got the same message. Whoever replies properly first usually wins it.",
        "Then come months of detail: times, set lists, access, clients changing their minds, suppliers to confirm.",
        "On the day nothing can be fixed after the fact, so nothing should depend on one person's phone.",
      ],
      whereItHurts: [
        "Answering every enquiry with “what date, and where?”",
        "Ringing round to check availability, and the odd double booking.",
        "Bookings held on a deposit nobody's paid.",
        "Three versions of the run sheet, all different.",
        "A ticket platform taking a cut and keeping your customers.",
      ],
      canBuild: [
        "An enquiry form that asks what a planner would ask",
        "Availability and booking, with the deposit taken there and then",
        "Ticketing on your own site, with payouts, transfers and wallet passes",
        "Door scanning someone else can run on the night",
        "Act and supplier profiles with video, edited by your own team",
        "Automatic client updates through the quiet months",
        "Run sheets in one place, readable on a phone at the venue",
        "Review requests sent while the night's still fresh",
      ],
      approach: [
        "The website and the back office get built as one thing. Winning the enquiry and then dumping it in a spreadsheet just moves the problem.",
        "You get the keys. Acts, venues and packages change, so you can edit it all yourself.",
      ],
      builtIds: [
        "multi-step-booking-sync",
        "artist-video-profiles",
        "gs-ticket-payouts",
        "gs-door-scan",
        "gs-ticket-wallet",
        "gs-ticket-transfer",
        "gs-event-analytics",
        "gs-calendar-sync",
        "a-page-for-every-place-you-work",
        "review-collection",
        "self-serve-cms",
      ],
      caseStudySlugs: ["the-dfc-experience"],
      relatedServiceSlugs: [
        "booking-systems",
        "website-design-and-development",
        "internal-business-systems",
      ],
      moreSpecific: {
        name: "Entertainment agencies and wedding entertainers",
        href: "/industries/entertainment-agencies",
        note: "The narrower page, for agencies and acts booking DJs, hosts and live performers.",
      },
    },
  },
  /*
   * GYMS, PROPERTY MAINTENANCE AND ENTERTAINMENT AGENCIES, added 2026-10-08 as search pages.
   *
   * Picked from Search Console, not guessed: the narrow "software for <trade>" pages already on
   * this site (lettings, aesthetics) reached positions 2 to 5 within weeks, while the broad
   * "custom business software" page sits around 79. Each of the three also has one of our own
   * demonstrations behind it, so the page can SHOW the software rather than describe it.
   *
   * Every demonstration named here is an invented company. Gyms and property maintenance rest on
   * rule B (no delivered work in the sector, so no proof is cited). Entertainment agencies rest on
   * rule A: The DFC Experience is real client work in exactly this market.
   */
  {
    slug: "gyms-and-leisure",
    href: "/industries/gyms-and-leisure",
    name: "Gyms and leisure",
    hubBlurb:
      "Machine faults, contractor repairs and shift checks for gym owners tired of the clipboard and the group chat.",
    hasDeliveredWork: false,
    page: {
      heading: "Software for gyms and leisure operators",
      metaTitle: "Gym maintenance and operations software | Ownly Digital",
      metaDescription:
        "Custom software for gym owners: equipment fault reports, contractor work orders and quotes, sign off before a machine goes back out, and shift handovers.",
      intro:
        "A machine's been taped off since Tuesday. You can't say whether the engineer came, what they did, or who decided it was safe again.",
      showcase: {
        heading: "What it looks like running",
        body: "Ironmere is a demonstration we built for an invented gym group with three clubs. The owner's screen shows how many machines are on the floor, the days lost to repairs, the quotes waiting on a decision and the checks that were missed. The phone screen is what a manager carries round the club, and it keeps working in a room with no signal.",
        demoName: "Ironmere",
        demoHref: "https://ironmere-demo.vercel.app",
        desktop: {
          src: "/industries/gyms-ironmere-overview.jpg",
          alt: "Ironmere owner overview across three clubs: 94.4 percent of machines available, 80.2 days lost in 90 days, quotes to decide, and a list of repairs and missed checks that need attention",
          width: 1800,
          height: 1050,
        },
        phone: {
          src: "/industries/gyms-ironmere-phone.jpg",
          alt: "Ironmere phone screen for a club manager, with buttons to scan or find a machine and a note that it is ready to work with no signal",
          width: 780,
          height: 1688,
        },
      },
      howTheyRun: [
        "Members pay for the kit. Every machine out of use is something they can't have, and they notice before you do.",
        "Whoever's on the floor spots the fault. A contractor fixes it, at a different club next week. The two rarely meet.",
        "Opening, cleaning and closing checks happen on paper, and paper can be ticked all at once at the end of the day.",
      ],
      whereItHurts: [
        "A broken machine reported three ways and logged nowhere.",
        "No idea how long that machine's been out of use.",
        "A contractor's quote sitting unapproved in someone's inbox.",
        "Repaired machines back on the floor with nobody checking.",
        "The cleaning sheet fully ticked at half five, jobs done or not.",
      ],
      canBuild: [
        "Fault reports made by scanning a code on the machine",
        "Work orders that follow each fault to a contractor, with the quote attached",
        "Spending limits, so a big quote waits for the right person",
        "A sign-off step: a repaired machine stays out of use until a named manager puts it back",
        "Days out of use counted per machine and per club",
        "Shift checklists split by the hour, with a proper handover",
        "Phone screens that work with no signal and catch up when it returns",
        "One view across every club for the owner",
      ],
      approach: [
        "We start with one broken machine, followed from the first report to back in use. That path runs through a coach, a manager and a contractor, and it's where the money goes.",
        "Staff will only use something quicker than a sign and some tape, so the phone side comes first, tested in the worst room in the building.",
      ],
      builtIds: [],
      caseStudySlugs: [],
      relatedServiceSlugs: [
        "internal-business-systems",
        "business-process-automation",
        "quoting-and-estimating",
      ],
      faq: [
        {
          question: "Can staff report a broken machine without sitting at a computer?",
          answer:
            "Yes. Each machine gets a printed code. Scanning it with a phone camera opens that machine's record, and the fault is reported from there with a photo. In our Ironmere demonstration the phone's own camera app opens the right record.",
        },
        {
          question: "Will it work in a basement with no phone signal?",
          answer:
            "It can be built to. The phone keeps the machines and checks it needs, carries on without a connection, and sends what was recorded once the signal returns. Our Ironmere demonstration is tested with the network switched off.",
        },
        {
          question: "Can it cover daily cleaning and safety checks as well as repairs?",
          answer:
            "Yes. Checks are grouped by the hour they belong to, signed with a name, and handed over at the end of the shift with anything missed carried forward. Our Shift demonstration shows this for gyms, salons, nurseries and care homes.",
        },
        {
          question: "We already have membership software. Does this replace it?",
          answer:
            "No. Membership software looks after members and payments. This is the operations side: the kit, the contractors and the checks. It is built to run beside what you already use for members.",
        },
      ],
      bookFirst: true,
    },
  },
  {
    slug: "property-maintenance",
    href: "/industries/property-maintenance",
    name: "Property maintenance",
    hubBlurb:
      "Job management for maintenance firms tired of extra work being agreed on site and never billed.",
    hasDeliveredWork: false,
    page: {
      heading: "Software for property maintenance companies",
      metaTitle: "Property maintenance job software | Ownly Digital",
      metaDescription:
        "Job management software for property maintenance firms: jobs from agents and landlords, extra work approved on site, photos, and invoices that match.",
      intro:
        "You found extra work on site, the customer said crack on, and nobody wrote it down. Now it's invoice time and you're either arguing over it or quietly dropping it.",
      showcase: {
        heading: "What it looks like running",
        body: "Brackenhall is a demonstration we built for an invented joinery and property maintenance firm. The office screen shows what is waiting on a price, what is sitting with customers and which job cannot be invoiced yet. The phone screen is the joiner's day, and it flags the job that is still missing its after photos.",
        demoName: "Brackenhall",
        demoHref: "https://ownly-brackenhall.vercel.app",
        desktop: {
          src: "/industries/property-maintenance-brackenhall-office.jpg",
          alt: "Brackenhall office overview: extra work approved this month, extra work waiting on a price and sitting with customers, jobs waiting to be given out and operatives on site",
          width: 1800,
          height: 1038,
        },
        phone: {
          src: "/industries/property-maintenance-brackenhall-phone.jpg",
          alt: "Brackenhall phone screen for a joiner, showing an open job with a warning that the after photos have not been added",
          width: 780,
          height: 1560,
        },
      },
      howTheyRun: [
        "Most work comes from a few big customers: letting agents, housing providers, landlords. Each one approves work its own way.",
        "The office books it, someone in a van does it, and someone who wasn't there approves it. Every handover is a chance to lose a detail.",
        "Finding extra work on site is normal. Getting it priced and agreed before you start isn't, and that gap is where the money goes.",
      ],
      whereItHurts: [
        "Retyping every job that came in by email, phone or text.",
        "Extra work agreed by text and lost by invoice time.",
        "Can't invoice because the after photos are on someone's phone.",
        "Ringing the van to find out where a job's up to.",
        "Every customer's approval rules living in one person's head.",
      ],
      canBuild: [
        "One record per job: the booking, photos, notes and every approval",
        "Extra work priced on site and approved by the customer from a link",
        "Jobs held back from invoicing while anything on them is unapproved or missing",
        "A phone screen for each operative with today's jobs, working with no signal",
        "Before and after photos saved against the job, not left in a camera roll",
        "Approval rules for each customer, kept in the system instead of in someone's head",
        "A view for each customer of their own jobs, so the status call stops",
        "Invoices built from the job, so the total matches what was agreed",
      ],
      approach: [
        "We start with one job that went wrong, like extra work that was never billed. Followed from the first call to the last payment, it shows where the record breaks.",
        "Operatives get the phone screen first, and it has to be quicker than texting the office or it won't get used.",
      ],
      builtIds: [],
      caseStudySlugs: [],
      relatedServiceSlugs: [
        "quoting-and-estimating",
        "internal-business-systems",
        "client-portals",
      ],
      faq: [
        {
          question: "Can customers approve extra work without downloading an app?",
          answer:
            "Yes. The price goes to them as a link. They open it on their phone, see the work and the photograph, and approve or decline. The answer is saved against the job with their name and the time, so it is there when the invoice goes out.",
        },
        {
          question: "What happens when an operative has no signal on site?",
          answer:
            "The phone keeps the day's jobs and lets them carry on. Notes and photographs are held on the phone and sent to the office as soon as the signal comes back.",
        },
        {
          question: "Can it pass finished jobs to our accounts package?",
          answer:
            "It can be built to, so nothing is typed twice. Which package you use is one of the first things we ask, because it decides how invoices leave the system.",
        },
      ],
      bookFirst: true,
    },
  },
  {
    slug: "entertainment-agencies",
    href: "/industries/entertainment-agencies",
    name: "Entertainment agencies and wedding entertainers",
    hubBlurb:
      "Bookings, client changes and performer briefs for agencies and acts, so the DJ has the same plan as the couple.",
    hasDeliveredWork: true,
    page: {
      heading: "Software for entertainment agencies and wedding entertainers",
      metaTitle: "Entertainment agency booking software | Ownly Digital",
      metaDescription:
        "Booking software for entertainment agencies and wedding DJs: enquiries into the diary, changes priced before they are added, and performer briefs.",
      intro:
        "The couple moved the first dance three weeks ago. The office knows, but your DJ finds out at the venue.",
      showcase: {
        heading: "What it looks like running",
        body: "Juniper is a demonstration we built for an invented wedding and events entertainment supplier. The office screen holds the diary, the clashes, the client requests waiting on a price and the briefs nobody has opened. The phone screen is the DJ's, with one brief per event and a version number on each.",
        demoName: "Juniper Events",
        demoHref: "https://juniper-events-one.vercel.app",
        desktop: {
          src: "/industries/entertainment-juniper-office.jpg",
          alt: "Juniper Events office screen: the next fortnight's bookings, a performer clash, a client request to review, briefs not yet acknowledged and the diary with fees for each event",
          width: 1800,
          height: 1025,
        },
        phone: {
          src: "/industries/entertainment-juniper-performer.jpg",
          alt: "Juniper Events performer screen for a DJ, showing two upcoming weddings, one brief acknowledged at version 3 and one where version 2 is new",
          width: 780,
          height: 1200,
        },
      },
      howTheyRun: [
        "You sell acts: DJs, hosts, bands, sax players. Each booking pairs a client with performers, often months ahead.",
        "Then the details change: times, extras, venue rules. Every change has to match in the office, with the client and with the performer.",
        "On the night the performer is on their own, often with poor signal, working from whatever they were last sent.",
      ],
      whereItHurts: [
        "Enquiries with no date, no venue and no budget.",
        "An extra hour asked for on WhatsApp and never priced.",
        "Your act turning up with last month's running order.",
        "Two acts pencilled in for the same Saturday.",
        "No idea whether the performer read the final brief.",
      ],
      canBuild: [
        "An enquiry form that asks what your office would ask and files the booking into your diary",
        "One diary showing every act, every date and every clash",
        "Client changes priced and accepted before they go into the plan",
        "A planning page for each couple, with their answers feeding the brief",
        "Numbered briefs, with performers confirming they've read the latest",
        "A performer screen that still opens with no signal at the venue",
        "Deposits, balances and what's left to collect against each booking",
        "Artist pages with video, edited by your own team",
      ],
      approach: [
        "We start with one booking that changed after it was confirmed, followed from the request to the night. It shows exactly where the versions split.",
        "The couple's page and the performer's screen get built alongside the office. A brief nobody reads on the night isn't finished.",
      ],
      builtIds: [
        "multi-step-booking-sync",
        "artist-video-profiles",
        "review-collection",
        "self-serve-cms",
      ],
      caseStudySlugs: ["the-dfc-experience"],
      relatedServiceSlugs: [
        "booking-systems",
        "client-portals",
        "internal-business-systems",
      ],
      faq: [
        {
          question: "Can couples see their booking without ringing the office?",
          answer:
            "Yes. Each couple gets their own page with their planning form, their running order and a way to ask for a change. A change they ask for is priced and accepted before it goes into the plan.",
        },
        {
          question: "How do I know a performer has read the latest brief?",
          answer:
            "Every brief has a version number. When a new version is published the performer is asked to confirm it, and the office can see who has confirmed and who has not.",
        },
        {
          question: "Have you built for an entertainment business before?",
          answer:
            "Yes. We built the booking site for The DFC Experience, a wedding and event entertainment company: artist pages with video, a booking form that asks what a planner would ask, and a studio their own team uses to edit the site.",
        },
      ],
      bookFirst: true,
    },
  },
  /*
   * ESTATE AGENTS AND CLINICS, added 2026-09-19 because both are call campaign industries in the
   * September marketing plan, and its rule is that a campaign's page is live before the campaign
   * starts. Both rest on rule B (targeted market, real capability, no delivered work), so no proof
   * is cited. Never claim a portal integration (Rightmove, Zoopla) or a clinical compliance
   * standard here: neither has been built, and both are things a buyer would check.
   */
  {
    slug: "estate-and-letting-agents",
    href: "/industries/estate-and-letting-agents",
    name: "Estate and letting agents",
    hubBlurb:
      "Enquiries answered while the office is shut and viewings booked without the phone tag, for agents tired of losing to whoever replied first.",
    hasDeliveredWork: false,
    page: {
      heading: "Software for estate and letting agents",
      metaTitle: "Software for estate and letting agents | Ownly Digital",
      metaDescription:
        "Custom software for estate and letting agents: instant replies out of hours, viewing booking, vendor and landlord portals, and compliance reminders.",
      intro:
        "The enquiry came in at eight last night, and three other agents got it too. Whoever replied properly first has probably booked the viewing already.",
      howTheyRun: [
        "Enquiries come from the portals, your website and the phone, mostly evenings and weekends. Each one needs to become a viewing before interest goes cold.",
        "Every sale or let is a chain of people wanting updates: vendors, buyers, landlords, tenants, solicitors. Much of the week goes on telling them where things are.",
        "Lettings never stops: renewals, inspections, certificates and deposits, each with a date that matters.",
      ],
      whereItHurts: [
        "Evening enquiries getting their first reply next morning.",
        "Four messages and a phone call to book one viewing.",
        "Vendors ringing to ask how the viewings went.",
        "Certificate dates in a spreadsheet only one person keeps.",
        "Maintenance requests lost between texts, emails and calls.",
      ],
      canBuild: [
        "An instant reply to every enquiry, day or night, with real viewing slots",
        "Viewing booking that checks the diary and confirms itself",
        "A vendor portal showing viewings, feedback and offers as they happen",
        "A landlord portal with rent, maintenance, certificates and documents in one place",
        "Reminders for certificates, inspections and renewals, well before they're due",
        "Maintenance requests logged once, with photos, and passed to the right contractor",
        "Feedback captured on a phone straight after the viewing",
      ],
      approach: [
        "We start with the evening enquiry, followed from the moment it lands to a booked viewing. It's where most agents lose business.",
        "It works alongside the software your branch already uses, so nobody has to retype everything on day one.",
      ],
      builtIds: [],
      caseStudySlugs: [],
      relatedServiceSlugs: [
        "crm-development",
        "client-portals",
        "business-process-automation",
      ],
    },
  },
  {
    slug: "clinics-and-aesthetics",
    href: "/industries/clinics-and-aesthetics",
    name: "Clinics and aesthetics",
    hubBlurb:
      "Bookings, aftercare and reviews for clinics whose happiest clients never think to leave one.",
    hasDeliveredWork: false,
    page: {
      heading: "Software for clinics and aesthetics practices",
      metaTitle: "Software for clinics and aesthetics | Ownly Digital",
      metaDescription:
        "Custom software for clinics and aesthetics practices: online booking and deposits, consultation and consent forms, aftercare and review requests.",
      intro:
        "Your happiest clients walk out glowing and never write a word. The one unhappy client finds the review page all by themselves.",
      howTheyRun: [
        "A treatment starts before the appointment: enquiry, consultation, forms, sometimes a deposit. Every step is a chance for someone to go quiet and book elsewhere.",
        "Afterwards comes aftercare and, for many treatments, a return visit. That rebooking is where much of a clinic's income comes from, and it depends on someone remembering to ask.",
        "Client information is sensitive, so forms, notes and photos must only be seen by the people who need them.",
      ],
      whereItHurts: [
        "Happy clients leaving without being asked for a review.",
        "Consultation forms filled in on paper in the waiting room.",
        "Empty slots from no-shows nobody reminded.",
        "Rebooking dates kept in someone's memory.",
        "Before and after photos on a personal phone.",
      ],
      canBuild: [
        "Review requests after a good appointment, linking straight to the right review page",
        "Online booking with the deposit taken when the slot is chosen",
        "Consultation and consent forms done before the client arrives",
        "Reminders the day before, so fewer slots are lost to no-shows",
        "Aftercare messages and a nudge to rebook when the treatment is due again",
        "Client records with notes and photos, seen only by the right staff",
      ],
      approach: [
        "We start with what happens after the appointment. That's where reviews and rebookings are won, and usually nobody owns it.",
        "Who can see client information is decided at the start, not bolted on later.",
      ],
      builtIds: [],
      caseStudySlugs: [],
      relatedServiceSlugs: [
        "booking-systems",
        "business-process-automation",
        "website-design-and-development",
      ],
    },
  },
  {
    slug: "accountancy",
    /* Narrower than professional services, older than this registry, and the one with the
       delivered work. Listed so it stays discoverable rather than being quietly demoted. */
    href: "/accountants",
    name: "Accountancy practices",
    hubBlurb:
      "The narrower professional services page, for practices competing on trust rather than price.",
    hasDeliveredWork: true,
  },
];

/**
 * EVERY REFERENCE CHECKED AT IMPORT. Same reasoning as `services.ts`: a proof link is a
 * promise that a real thing sits behind it, and these pages are read by people deciding
 * whether to believe us. A renamed catalogue id should stop the build, not quietly shorten
 * the evidence.
 */
const BUILT_IDS = new Set(
  BUILT_GROUPS.flatMap((group) => group.items.map((item) => item.id)),
);
const CASE_STUDY_SLUGS = new Set(CASE_STUDIES.map((study) => study.slug));
const SERVICE_SLUGS = new Set(SERVICES.map((service) => service.slug));

for (const industry of INDUSTRIES) {
  const where = `industries.ts: ${industry.slug}`;
  if (!industry.page) continue;
  for (const id of industry.page.builtIds) {
    if (!BUILT_IDS.has(id)) {
      throw new Error(`${where} cites built entry "${id}", which does not exist`);
    }
  }
  for (const slug of industry.page.caseStudySlugs) {
    if (!CASE_STUDY_SLUGS.has(slug)) {
      throw new Error(`${where} cites case study "${slug}", which does not exist`);
    }
  }
  for (const slug of industry.page.relatedServiceSlugs) {
    if (!SERVICE_SLUGS.has(slug)) {
      throw new Error(`${where} links to service "${slug}", which does not exist`);
    }
  }
  /*
   * The honesty invariant, enforced rather than trusted. `hasDeliveredWork` decides what the
   * hub is allowed to say about a page, so it cannot be allowed to drift away from whether
   * any delivered work is actually cited.
   */
  const cites =
    industry.page.builtIds.length > 0 || industry.page.caseStudySlugs.length > 0;
  if (cites !== industry.hasDeliveredWork) {
    throw new Error(
      `${where} says hasDeliveredWork=${industry.hasDeliveredWork} but ${
        cites ? "cites" : "cites no"
      } delivered work`,
    );
  }
}

/** The industries that get a generated page under `/industries/`. */
export const INDUSTRIES_WITH_PAGES = INDUSTRIES.filter(
  (industry): industry is Industry & { page: IndustryPage } => Boolean(industry.page),
);

export function getIndustry(slug: string): Industry | undefined {
  return INDUSTRIES.find((industry) => industry.slug === slug);
}

export function builtItemsForIndustry(page: IndustryPage) {
  const items = BUILT_GROUPS.flatMap((group) =>
    group.items.map((item) => ({ ...item, group })),
  );
  return page.builtIds
    .map((id) => items.find((item) => item.id === id))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
}

export function caseStudiesForIndustry(page: IndustryPage) {
  return page.caseStudySlugs
    .map((slug) => CASE_STUDIES.find((study) => study.slug === slug))
    .filter((study): study is NonNullable<typeof study> => Boolean(study));
}

export function servicesForIndustry(page: IndustryPage) {
  return page.relatedServiceSlugs
    .map((slug) => SERVICES.find((service) => service.slug === slug))
    .filter((service): service is NonNullable<typeof service> => Boolean(service));
}
